import { Injectable } from '@angular/core';

export enum ThemeType {
  LIGHT = 'light',
  DARK = 'dark',
}

export enum FontType {
  Manrope = 'Manrope',
  Times = 'Times',
}

export enum ThemeName {
  DARK_Manrope = 'dark-Manrope',
  DARK_Times = 'dark-Times',
  LIGHT_Manrope = 'light-Manrope',
  LIGHT_Times = 'light-Times'
}

export type ExtraThemeType = {
  name: ThemeName,
  theme: ThemeType,
  font: FontType
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  constructor() {}

  selectTheme(theme: ThemeType, font: FontType): void {
    const html = document.getElementsByTagName('html')[0];
    html.setAttribute('data-theme', theme);
    html.setAttribute('data-font', font);
    this.loadTheme(theme);
  }

  selectThemeByName(themeName: ThemeName): void {
    switch (themeName) {
      case ThemeName.DARK_Manrope:
        this.selectTheme(ThemeType.DARK, FontType.Manrope);
        break;
      case ThemeName.DARK_Times:
        this.selectTheme(ThemeType.DARK, FontType.Times);
        break;
      case ThemeName.LIGHT_Manrope:
        this.selectTheme(ThemeType.LIGHT, FontType.Manrope);
        break;
      case ThemeName.LIGHT_Times:
        this.selectTheme(ThemeType.LIGHT, FontType.Times);
        break;
      default:
        this.selectTheme(ThemeType.LIGHT, FontType.Manrope);
        break;
    }
  }

  getCurrentTheme(): string {
    const theme = document.getElementsByTagName('html')[0].getAttribute('data-theme');
    return theme || 'light';
  }

  getCurrentFontFamily(): string {
    const font = document.getElementsByTagName('html')[0].getAttribute('data-font');
    switch (font) {
      case FontType.Manrope:
        return "'Manrope', Arial, Helvetica, sans-serif";
      case FontType.Times:
        return "'Times New Roman', Times, serif"
      default:
        return "'Manrope', Arial, Helvetica, sans-serif";
    }
  }

  getApexChartThemeType(): 'light' | 'dark' {
    switch (this.getCurrentTheme()) {
      case ThemeType.LIGHT:
        return 'light';
      case ThemeType.DARK:
        return 'dark';
      default:
        return 'light';
    }
  }

  private removeUnusedTheme(theme: ThemeType): void {
    document.documentElement.classList.remove(theme);
    const removedThemeStyle = document.getElementById(theme);
    if (removedThemeStyle) {
      document.head.removeChild(removedThemeStyle);
    }
  }

  private loadCss(href: string, id: string): Promise<Event> {
    return new Promise((resolve, reject) => {
      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.href = href;
      style.id = id;
      style.onload = resolve;
      style.onerror = reject;
      document.head.append(style);
    });
  }

  public loadTheme(theme: ThemeType): Promise<Event> {
    return new Promise<Event>((resolve, reject) => {
      this.loadCss(`${theme}.css`, theme).then(
        (e) => {
          document.documentElement.classList.add(theme);
          this.removeUnusedTheme(theme === ThemeType.LIGHT ? ThemeType.DARK : ThemeType.LIGHT);
          resolve(e);
        },
        (e) => reject(e)
      );
    });
  }
}
